/**
 * The demo's approval chain, kept the way the platform keeps it (build 4).
 *
 * Under the mock the Approvals board still draws its own four sample requests
 * and its own acting-role picker, exactly as before — it never calls this.
 * This store is here so the sample answers every method the platform does,
 * and refuses what the platform refuses, in the platform's words: a request
 * with no value, kind or reason; a step the person does not hold; their own
 * request; a second step by the same person; a rejection with no reason; an
 * order for a request not yet approved.
 *
 * It starts empty and lives for the session.
 */
import { DataSourceError } from "../api/dataSource";
import {
  APPROVAL_STEPS,
  nextStep,
  raiseRefusal,
  stepRefusal,
  type ApprovalRequest,
  type ApprovalsBoard,
  type ApprovalStepAck,
  type NewApprovalRequest,
} from "../domain/approvals";

export interface MockApprover {
  name: string;
  holds: (permission: string) => boolean;
}

interface Held extends Omit<ApprovalRequest, "next" | "mayAct" | "whyNot"> {
  siteId: string;
}

const held: Held[] = [];
/* what a key was answered with, so a retried write is answered the same way */
const answered = new Map<string, unknown>();

function replay<T>(key: string | undefined, run: () => T): T {
  if (key && answered.has(key)) return { ...(answered.get(key) as T), replayed: true };
  const out = run();
  if (key) answered.set(key, out);
  return out;
}

const year = () => new Date().getFullYear();
let rqSeq = 0;
let poSeq = 0;

function find(siteId: string, number: string): Held {
  const q = held.find((x) => x.siteId === siteId && x.number === number);
  if (!q) throw new DataSourceError(`${number} is not on the register.`, { status: 404 });
  return q;
}

export function mockApprovalsBoard(siteId: string, me: MockApprover): ApprovalsBoard {
  const requests = held
    .filter((q) => q.siteId === siteId)
    .slice()
    .reverse()
    .map((q): ApprovalRequest => {
      const { siteId: _s, ...rest } = q;
      const step = nextStep(q.steps);
      const refused = q.status === "submitted" ? stepRefusal(q, me) : null;
      return {
        ...rest,
        next: q.status === "submitted" && step ? { key: step.key, label: step.label, who: step.who } : null,
        mayAct: q.status === "submitted" && !!step && !refused,
        ...(refused ? { whyNot: refused.why } : {}),
      };
    });
  return {
    currency: "SAR",
    me: me.name,
    chain: APPROVAL_STEPS.map((s) => ({ ...s, mine: me.holds(s.permission) })),
    can: { raise: me.holds("stores:write"), order: me.holds("stores:approve") },
    requests,
    awaitingMe: requests.filter((q) => q.mayAct).map((q) => q.number),
    stores: { waiting: 0, approved: 0 },
  };
}

export function mockRaiseApprovalRequest(
  siteId: string,
  input: NewApprovalRequest,
  me: MockApprover,
  key?: string,
): { number: string; replayed?: boolean } {
  if (!me.holds("stores:write")) throw new DataSourceError("missing permission 'stores:write'", { status: 403 });
  return replay(key, () => {
    const why = raiseRefusal(input);
    if (why) throw new DataSourceError(why, { status: 400 });
    const number = `RQ-${year()}-${String(++rqSeq).padStart(4, "0")}`;
    held.push({
      siteId,
      number,
      kind: input.kind,
      title: input.title.trim(),
      ...(input.supplier?.trim() ? { supplier: input.supplier.trim() } : {}),
      value: input.value as number,
      currency: "SAR",
      why: input.why.trim(),
      ...(input.source?.trim() ? { source: input.source.trim() } : {}),
      emergency: !!input.emergency,
      raisedAt: new Date().toISOString(),
      raisedBy: me.name,
      status: "submitted",
      steps: [],
    });
    return { number };
  });
}

export function mockSignApprovalStep(
  siteId: string,
  number: string,
  decision: "approved" | "rejected",
  note: string | undefined,
  me: MockApprover,
  key?: string,
): ApprovalStepAck {
  return replay(key, () => {
    if (decision !== "approved" && decision !== "rejected")
      throw new DataSourceError("A decision is approved or rejected.", { status: 400 });
    if (decision === "rejected" && !note?.trim())
      throw new DataSourceError("A rejection must say why. A rejection with no reason cannot be answered.", {
        status: 400,
      });
    const q = find(siteId, number);
    const refused = stepRefusal(q, me);
    if (refused) throw new DataSourceError(refused.why, { status: refused.status });
    const step = nextStep(q.steps)!;
    q.steps.push({
      step: step.key,
      decision,
      by: me.name,
      at: new Date().toISOString(),
      ...(note?.trim() ? { note: note.trim() } : {}),
      ...(step.limit != null ? { limit: step.limit } : {}),
      over: decision === "approved" && step.limit != null && q.value > step.limit,
    });
    const last = step.key === APPROVAL_STEPS[APPROVAL_STEPS.length - 1].key;
    q.status = decision === "rejected" ? "rejected" : last ? "approved" : "submitted";
    return {
      number,
      step: step.key,
      decision,
      status: q.status,
      over: step.limit != null && q.value > step.limit,
    };
  });
}

export function mockOrderApprovalRequest(
  siteId: string,
  number: string,
  supplier: string | undefined,
  me: MockApprover,
  key?: string,
): { number: string; replayed?: boolean } {
  if (!me.holds("stores:approve")) throw new DataSourceError("missing permission 'stores:approve'", { status: 403 });
  return replay(key, () => {
    const q = find(siteId, number);
    if (q.status !== "approved")
      throw new DataSourceError(`${number} is ${q.status}. Only an approved requisition becomes an order.`, {
        status: 409,
      });
    const to = supplier?.trim() || q.supplier;
    if (!to) throw new DataSourceError("An order must name the supplier it goes to.", { status: 400 });
    const po = `PO-${year()}-${String(++poSeq).padStart(4, "0")}`;
    q.order = { number: po, at: new Date().toISOString(), by: me.name, supplier: to, value: q.value, status: "open" };
    q.status = "ordered";
    return { number: po };
  });
}

/** For the tests: start again. */
export function resetMockApprovals(): void {
  held.length = 0;
  answered.clear();
  rqSeq = 0;
  poSeq = 0;
}
